
// DANVA Workspace panel (task section 13-17/23/24/28/29/47) - mounted into
// the shared right-side SideRail (see viewer.js/sidebar.js), the same
// mechanic Hjælp already uses; never a standalone modal (task section 13).
//
// Level 2/3 (DOM, needs a live DigitizationModule) - no unit tests here by
// this project's own established convention (see CLAUDE.md's Test-hierarki;
// every other DOM-heavy panel in this module - TopologyQaPanel.js,
// HelpPanel.js, VectorEditingMenu.js's prompts - is verified live instead).
// All the logic this panel calls into (classification, validation, mapping,
// relations, exchange) is the separately unit-tested pure layer in the
// sibling danva/*.js files.
import JSZip from "jszip";
import {createUnavailableDanvaModelProvider} from "./DanvaModelProvider.js";
import {isDanvaObject, getDanvaData, classifyAsDanva, unclassifyDanva, setDanvaAttributes} from "./DanvaDomainData.js";
import {listCompatibleObjectTypes} from "./DanvaCompatibility.js";
import {validateGeometry, validateDanvaObject} from "./DanvaValidation.js";
import {addRelation, removeRelation, findDanglingRelations, suggestRelationsFromTopology} from "./DanvaRelations.js";
import {validateForDanvandExport, buildDanvandExport} from "./DanvaExchangeAdapter.js";
import {buildExportBasename} from "../export/exportFilename.js";

// Module-level singleton (task section 44: a project only ever has ONE
// active DANVA model/version at a time) - mirrors the same
// singleton-store-module convention DigitizationSettings.js's own exported
// `settings` already uses elsewhere in this codebase, rather than a second
// pattern. Real model data plugs in here later by replacing
// createUnavailableDanvaModelProvider() with
// createDanvaModelProviderFromModelData(...) once licensed files exist -
// the ONE line every part of this panel would need to change.
let modelProvider = createUnavailableDanvaModelProvider();
let activeModel = null; // {model, version} | null - task section 7/44

// The one integration point for when a real, licensed model becomes
// available (task section 44/17) - swaps the shared provider and sets the
// project's active model/version explicitly (never a boolean, see
// DanvaDomainData.js's own header comment). With today's zero-model reality
// there is nothing meaningful to expose as a "choose active model" dropdown
// (see the task's own final report, section H) - this function IS that
// integration point once ≥1 real model exists; a trivial `<select>` bound to
// `modelProvider.listModels()` is the only UI still needed at that point.
export function configureDanvaModel(provider, model, version){
	modelProvider = provider;
	activeModel = model ? {model, version} : null;
}

function statusIcon(ok){
	if(ok === true) return "✓";
	if(ok === false) return "✕";
	return "⚠";
}

function section(title, contentEl){
	const details = document.createElement("details");
	details.open = true;
	const summary = document.createElement("summary");
	summary.textContent = title;
	summary.style.cssText = "cursor:pointer; font-weight:bold; font-size:12px; margin:10px 0 4px; color:#ccc;";
	details.appendChild(summary);
	details.appendChild(contentEl);
	return details;
}

function row(label, valueText){
	const el = document.createElement("div");
	el.style.cssText = "font-size:12px; display:flex; justify-content:space-between; gap:8px; padding:2px 0;";
	el.innerHTML = `<span style="opacity:0.75;">${label}</span><span>${valueText}</span>`;
	return el;
}

function button(text, onClick, opts = {}){
	const btn = document.createElement("button");
	btn.textContent = text;
	btn.disabled = !!opts.disabled;
	btn.style.cssText = `padding:4px 10px; border-radius:3px; border:none; background:${opts.disabled ? "#444" : "#2f8fd6"}; color:#fff; cursor:${opts.disabled ? "default" : "pointer"}; font-size:12px; opacity:${opts.disabled ? "0.6" : "1"};`;
	if(!opts.disabled) btn.addEventListener("click", onClick);
	return btn;
}

// Computes the current project's aggregate DANVA status (task section 14) -
// each DANVA-classified feature is "valid" (geometry AND logical validation
// both pass), "incomplete" (logical validation ran and found issues, but no
// hard error - here: same as invalid, this project does not yet distinguish
// "incomplete" from "error" beyond what the model itself reports, since no
// real model exists to define that distinction), or "error" (geometry
// invalid). With no model loaded, logical validation cannot run at all - see
// reason==="model-unavailable" - counted separately so the summary stays
// honest rather than reporting fake pass/fail numbers.
function computeProjectSummary(dm){
	const danvaFeatures = dm.featureManager.features.filter(isDanvaObject);
	let valid = 0, incomplete = 0, error = 0, modelUnavailable = 0;

	for(const feature of danvaFeatures){
		const geo = validateGeometry(feature);
		if(!geo.valid){ error++; continue; }

		const logical = validateDanvaObject(feature, modelProvider);
		if(logical.reason === "model-unavailable"){ modelUnavailable++; continue; }
		if(logical.valid) valid++; else incomplete++;
	}

	return {total: danvaFeatures.length, valid, incomplete, error, modelUnavailable};
}

function renderProjectOverview(container, dm){
	container.innerHTML = "";

	const title = document.createElement("div");
	title.style.cssText = "font-weight:bold; font-size:14px; margin-bottom:8px;";
	title.textContent = "DANVA";
	container.appendChild(title);

	if(!modelProvider.hasAnyModel()){
		const notice = document.createElement("div");
		notice.style.cssText = "font-size:12px; opacity:0.85; line-height:1.5; background:#2f2f2f; border-radius:4px; padding:8px 10px; margin-bottom:10px;";
		notice.innerHTML = "Ingen DANVA-model er indlæst i dette miljø.<br><br>" +
			"De officielle Danvand-/Dandas-/Fælles Kernemodel-filer distribueres kun via den licenserede \"DANVA-Reolen\"-portal - " +
			"læg de licenserede model-/kodeliste-filer i projektet for at aktivere DANVA-registrering (se den afsluttende rapport for den præcise integrationsvej).";
		container.appendChild(notice);
	}

	const summary = computeProjectSummary(dm);
	const summaryBox = document.createElement("div");
	summaryBox.appendChild(row("Model", activeModel ? `${activeModel.model} ${activeModel.version}` : "Ikke konfigureret"));
	summaryBox.appendChild(row("Objekter", String(summary.total)));
	summaryBox.appendChild(row("Valide", String(summary.valid)));
	summaryBox.appendChild(row("Ufuldstændige", String(summary.incomplete)));
	summaryBox.appendChild(row("Fejl", String(summary.error)));
	if(summary.modelUnavailable > 0) summaryBox.appendChild(row("Afventer model", String(summary.modelUnavailable)));
	container.appendChild(summaryBox);

	const actions = document.createElement("div");
	actions.style.cssText = "display:flex; gap:6px; margin:10px 0;";
	actions.appendChild(button("Valider alle", () => renderProjectOverview(container, dm)));
	actions.appendChild(button("Importér", () => {}, {disabled: true}));
	container.appendChild(actions);

	container.appendChild(section("DANVAND 2.0.0-eksport", renderDanvandExportSection(dm, container)));

	if(summary.total > 0){
		const listTitle = document.createElement("div");
		listTitle.style.cssText = "font-size:12px; font-weight:bold; margin-top:8px; opacity:0.85;";
		listTitle.textContent = "Registreringer / problemer";
		container.appendChild(listTitle);

		const list = document.createElement("div");
		for(const feature of dm.featureManager.features.filter(isDanvaObject)){
			const geo = validateGeometry(feature);
			const logical = validateDanvaObject(feature, modelProvider);
			const ok = geo.valid && (logical.reason === "model-unavailable" ? null : logical.valid);

			const itemRow = document.createElement("div");
			itemRow.style.cssText = "font-size:12px; padding:4px 0; cursor:pointer; border-bottom:1px solid #ffffff14; display:flex; justify-content:space-between;";
			itemRow.innerHTML = `<span>${statusIcon(ok)} ${feature.properties.name}</span>`;
			// Task section 29: clicking a problem selects + focuses it and opens its DANVA data.
			itemRow.addEventListener("click", () => {
				const visual = dm.getVisual(feature.id);
				if(visual) dm.selectVisual(visual);
			});
			list.appendChild(itemRow);
		}
		container.appendChild(list);
	}
}

// Task UI requirement: "se om projektets DANVA-data er klar til officiel
// eksport... se konkrete valideringsfejl før eksport... starte den
// officielle DANVAND XML-export, når datasættet er gyldigt." Reuses
// DanvaExchangeAdapter.validateForDanvandExport() directly - the readiness
// shown here and the readiness buildDanvandExport() itself checks before
// serializing can never disagree, since it's the exact same function.
function renderDanvandExportSection(dm, root){
	const box = document.createElement("div");
	const danvaFeatures = dm.featureManager.features.filter(isDanvaObject);
	const result = validateForDanvandExport(danvaFeatures, dm.featureManager);

	box.appendChild(row("Klar til officiel eksport", result.ready ? "✓ Ja" : "✕ Nej"));
	box.appendChild(row("Knude / Ledning", `${result.knudeCount} / ${result.ledningCount}`));

	if(result.issues.length > 0){
		const issueTitle = document.createElement("div");
		issueTitle.style.cssText = "font-size:11px; font-weight:bold; color:#ff8080; margin-top:6px;";
		issueTitle.textContent = "Blokerer eksport:";
		box.appendChild(issueTitle);
		for(const issue of result.issues){
			const line = document.createElement("div");
			line.style.cssText = "font-size:11px; color:#ff8080;";
			line.textContent = `${issue.featureName}: ${issue.message}`;
			box.appendChild(line);
		}
	}

	// The FunktionStruktur warning (see DanvaExchangeAdapter.js's own header
	// comment) repeats once per Knude - collapsed to distinct MESSAGES here
	// so the user sees the finding once, not N times for N Knuder.
	const distinctWarnings = [...new Map((result.warnings || []).map((w) => [w.message, w])).values()];
	if(distinctWarnings.length > 0){
		const warnTitle = document.createElement("div");
		warnTitle.style.cssText = "font-size:11px; font-weight:bold; color:#ffb347; margin-top:6px;";
		warnTitle.textContent = "Kendte begrænsninger (blokerer ikke eksport):";
		box.appendChild(warnTitle);
		for(const warning of distinctWarnings){
			const line = document.createElement("div");
			line.style.cssText = "font-size:11px; color:#ffb347;";
			line.textContent = warning.message;
			box.appendChild(line);
		}
	}

	box.appendChild(button("Eksportér DANVAND XML", () => runOfficialDanvandExport(dm), {disabled: !result.ready}));
	return box;
}

async function runOfficialDanvandExport(dm){
	const danvaFeatures = dm.featureManager.features.filter(isDanvaObject);
	const viewer = dm.viewer;
	const pointcloudName = viewer.getPointcloudName ? viewer.getPointcloudName() : null;
	const exportDate = new Date();

	const result = buildDanvandExport(danvaFeatures, dm.featureManager, {pointcloudName, exportDate});
	if(!result.ready || !result.documents || result.documents.length === 0) return; // defensive - button is disabled otherwise

	let blob, filename;
	if(result.documents.length === 1){
		blob = new Blob([result.documents[0].text], {type: "application/xml"});
		filename = result.documents[0].filename;
	}else{
		// Task's own established mixed-output convention (see the earlier SHP
		// exporter's mixed-geometry ZIP) - DANVAND 2.0.0 has no combining
		// document type (see DanvaExchangeAdapter.js's own header comment), so
		// KnudeSamling.xml + LedningSamling.xml are bundled as one ZIP download.
		const zip = new JSZip();
		for(const doc of result.documents) zip.file(doc.filename, doc.text);
		const arrayBuffer = await zip.generateAsync({type: "arraybuffer", compression: "DEFLATE"});
		blob = new Blob([arrayBuffer], {type: "application/zip"});
		filename = `${buildExportBasename(pointcloudName, exportDate)}_DANVAND.zip`;
	}

	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function renderRegistrationOffer(container, dm, feature, visual){
	const box = document.createElement("div");
	box.style.cssText = "font-size:12px; line-height:1.6;";
	box.appendChild(row("Valgt", feature.properties.name));

	const notice = document.createElement("div");
	notice.style.cssText = "opacity:0.85; margin:6px 0;";
	notice.textContent = "Ikke registreret som DANVA-objekt.";
	box.appendChild(notice);

	if(!modelProvider.hasAnyModel() || !activeModel){
		const info = document.createElement("div");
		info.style.cssText = "opacity:0.7; font-style:italic;";
		info.textContent = "Ingen DANVA-model konfigureret - kan ikke tilbyde objekttyper endnu.";
		box.appendChild(info);
		container.appendChild(box);
		return;
	}

	const compatible = listCompatibleObjectTypes(modelProvider, activeModel.model, activeModel.version, feature.geometry.type);
	const select = document.createElement("select");
	for(const type of compatible){
		const option = document.createElement("option");
		option.value = type.id;
		option.textContent = type.label;
		select.appendChild(option);
	}
	box.appendChild(select);

	box.appendChild(button("Registrér", () => {
		classifyAsDanva(dm.featureManager, feature.id, {model: activeModel.model, version: activeModel.version, objectType: select.value});
		renderDanvaPanelContent(container, dm);
	}, {disabled: compatible.length === 0}));

	container.appendChild(box);
}

// `root`: the panel's OWN top-level container (see renderDanvaPanelContent()'s
// `container` param) - threaded through explicitly rather than derived via
// box.closest()/container.parentElement (a real bug found live: those
// resolved to the wrong ancestor and either re-rendered nothing or the wrong
// DOM subtree - see this session's own runtime-verification findings).
// Passing the same root reference everywhere a re-render is triggered from a
// button click is the simplest fix that can't drift out of sync with the
// panel's actual DOM structure.
// Task's own explicit gap (see this project's own header comment history):
// the OLD version of this section had exactly ONE generic "Vælg i
// viewer…" button that always recorded the relation under a single
// hardcoded name ("Til") - which cannot represent an object type like
// Ledning that needs TWO distinctly-named relations to the SAME target
// object type (Startknude vs. Slutknude - see the generated Danvand 2.0.0
// model's own Ledning.relations). Generalized here to render one row PER
// NAMED relation slot the ACTIVE MODEL defines for this object type
// (`def.relations`, model-driven - never a hardcoded per-object-type list,
// so a future model version's differently-named relations need no code
// change here). Any relation actually present on the feature that doesn't
// match one of the model's current named slots (e.g. legacy data, or a
// slot removed in a newer model version) is still shown, never silently
// dropped, under its own "Andre relationer" heading.
function renderRelationsSection(dm, feature, root){
	const data = getDanvaData(feature);
	const def = modelProvider.getObjectDefinition(data.model, data.version, data.objectType);
	const relationDefs = def ? def.relations : [];
	const namedSlots = new Set(relationDefs.map((r) => r.name));
	const box = document.createElement("div");

	for(const relDef of relationDefs){
		const existing = data.relations.find((r) => r.name === relDef.name);
		const target = existing ? dm.featureManager.get(existing.targetFeatureId) : null;
		const label = relDef.required ? `${relDef.name} *` : relDef.name;

		const relRow = document.createElement("div");
		relRow.style.cssText = "font-size:12px; display:flex; justify-content:space-between; align-items:center; padding:2px 0;";

		if(existing){
			const nameSpan = document.createElement("span");
			nameSpan.textContent = `${label}: ${target ? target.properties.name : "(manglende objekt)"}`;
			if(!target) relRow.style.color = "#ff8080";
			relRow.appendChild(nameSpan);
			const removeBtn = button("✕", () => { removeRelation(dm.featureManager, feature, existing); renderDanvaPanelContent(root, dm); }, {});
			removeBtn.style.cssText += "padding:1px 6px;";
			relRow.appendChild(removeBtn);
		}else{
			const nameSpan = document.createElement("span");
			nameSpan.style.cssText = "opacity:0.7;";
			nameSpan.textContent = `${label}: mangler`;
			relRow.appendChild(nameSpan);
			const pickBtn = button("Vælg i viewer…", () => startRelationPicking(dm, feature, relDef.name, () => renderDanvaPanelContent(root, dm)));
			pickBtn.style.cssText += "padding:1px 6px;";
			relRow.appendChild(pickBtn);
		}
		box.appendChild(relRow);
	}

	const otherRelations = data.relations.filter((r) => !namedSlots.has(r.name));
	if(otherRelations.length > 0){
		const otherTitle = document.createElement("div");
		otherTitle.style.cssText = "font-size:11px; opacity:0.7; margin-top:8px;";
		otherTitle.textContent = "Andre relationer:";
		box.appendChild(otherTitle);
		for(const rel of otherRelations){
			const target = dm.featureManager.get(rel.targetFeatureId);
			const relRow = document.createElement("div");
			relRow.style.cssText = "font-size:12px; display:flex; justify-content:space-between; align-items:center; padding:2px 0;";
			relRow.innerHTML = `<span>${rel.name} → ${target ? target.properties.name : "(manglende objekt)"}</span>`;
			if(!target) relRow.style.color = "#ff8080";
			const removeBtn = button("✕", () => { removeRelation(dm.featureManager, feature, rel); renderDanvaPanelContent(root, dm); }, {});
			removeBtn.style.cssText += "padding:1px 6px;";
			relRow.appendChild(removeBtn);
			box.appendChild(relRow);
		}
	}

	// Suggestions fill whichever named slot is still empty (the first one,
	// if more than one is open) - falls back to the generic "Til" name only
	// when the active model defines no named relations at all for this
	// object type (e.g. a test/fixture model), matching the pre-existing
	// behaviour for that case.
	const suggestions = suggestRelationsFromTopology(feature, dm.featureManager.features);
	if(suggestions.length > 0){
		const openSlot = relationDefs.find((r) => !data.relations.some((rel) => rel.name === r.name));
		const suggestTitle = document.createElement("div");
		suggestTitle.style.cssText = "font-size:11px; opacity:0.7; margin-top:8px;";
		suggestTitle.textContent = openSlot
			? `Foreslåede relationer til "${openSlot.name}" (geometrisk sammenfald - ikke automatisk oprettet):`
			: "Foreslåede relationer (geometrisk sammenfald - ikke automatisk oprettet):";
		box.appendChild(suggestTitle);
		for(const s of suggestions){
			const sRow = document.createElement("div");
			sRow.style.cssText = "font-size:12px; display:flex; justify-content:space-between; padding:2px 0;";
			sRow.innerHTML = `<span>${s.targetFeatureName}</span>`;
			const acceptBtn = button("Tilføj", () => {
				addRelation(dm.featureManager, feature, {name: openSlot ? openSlot.name : "Til", targetFeatureId: s.targetFeatureId, relationType: s.reason});
				renderDanvaPanelContent(root, dm);
			}, {disabled: relationDefs.length > 0 && !openSlot});
			sRow.appendChild(acceptBtn);
			box.appendChild(sRow);
		}
	}

	const dangling = findDanglingRelations(feature, dm.featureManager);
	if(dangling.length > 0){
		const warn = document.createElement("div");
		warn.style.cssText = "font-size:11px; color:#ffb347; margin-top:6px;";
		warn.textContent = `${dangling.length} relation(er) peger på et objekt der ikke længere findes.`;
		box.appendChild(warn);
	}

	return box;
}

// Task section 24: "PointView går i en kontrolleret selection-mode og lader
// brugeren vælge et kompatibelt objekt... ESC skal annullere." Reuses the
// EXISTING selection system entirely - listens for the next
// "visual_selection_changed" dm already dispatches from every selection path
// (viewer click, sidebar tree - see DigitizationModule.selectVisual()), no
// separate DANVA selection-mode/state is invented (task section 26).
//
// Runtime-verification finding: sidebar.js's own pre-existing "3D -> list
// selection sync" listener (registered on this SAME "visual_selection_changed"
// event, at app startup - see its own doc comment there) reacts to a real
// selection change by calling `tree.jstree('deselect_all')` +
// `tree.jstree('select_node', ...)`, which re-enters jstree's own
// select/deselect event machinery synchronously. Observed live: after that
// reentrancy, this panel's own selection-driven rerender (registered on the
// same event, see mountDanvaPanel()) ends up not reflecting the final
// selection - confirmed via direct instrumentation (the underlying data -
// the relation itself - was always correct; only this panel's OWN display
// went stale). Rather than depend on winning a race against another,
// pre-existing module's reentrant DOM manipulation, `onDone` is called
// explicitly and unconditionally once picking finishes (success or ESC-
// cancel), guaranteeing this panel's own view is always correct regardless
// of how many other listeners the shared selection event triggers.
let relationPicking = null;

function startRelationPicking(dm, sourceFeature, relationName, onDone){
	if(relationPicking) stopRelationPicking(dm);

	dm._showInstruction("DANVA relation", `Vælg det objekt "${relationName}" skal pege på i vieweren. ESC annullerer.`);

	const onSelectionChanged = (e) => {
		const targetVisual = e.visuals[0];
		if(!targetVisual) return;
		const targetFeature = dm.getFeature(targetVisual);
		if(!targetFeature || targetFeature.id === sourceFeature.id) return;

		addRelation(dm.featureManager, sourceFeature, {name: relationName, targetFeatureId: targetFeature.id, relationType: "manual"});
		stopRelationPicking(dm);
	};
	const onKeydown = (e) => { if(e.key === "Escape") stopRelationPicking(dm); };

	dm.addEventListener("visual_selection_changed", onSelectionChanged);
	window.addEventListener("keydown", onKeydown);
	relationPicking = {onSelectionChanged, onKeydown, onDone};
}

function stopRelationPicking(dm){
	if(!relationPicking) return;
	const {onSelectionChanged, onKeydown, onDone} = relationPicking;
	dm.removeEventListener("visual_selection_changed", onSelectionChanged);
	window.removeEventListener("keydown", onKeydown);
	dm._hideToolHint();
	relationPicking = null;
	if(onDone) onDone();
}

// Task UI requirement (runtime-verification step 4: "udfylde de nødvendige
// obligatoriske DANVAND-felter") - the OLD version of this section only
// ever displayed attributes read-only, with no way to actually fill in the
// required grunddata a real export needs. One generic, type-aware input per
// attribute, driven entirely by the active model's own `attr.dataType`
// ("code"/"boolean"/"date"/other) - never a per-attribute-name special
// case. Values are written back via the existing setDanvaAttributes() path
// (task section 20: provenance recorded as "user-entered", same as any
// other edit), keyed by the attribute's stable schema field name
// (`attr.name`), never its display label - exactly what
// DanvaExchangeAdapter.js's mapping functions read back out.
function renderAttributeRow(dm, feature, data, attr, root){
	const wrap = document.createElement("div");
	wrap.style.cssText = "font-size:12px; display:flex; justify-content:space-between; align-items:center; gap:8px; padding:2px 0;";

	const label = document.createElement("span");
	label.style.cssText = "opacity:0.75; flex-shrink:0;";
	label.textContent = attr.required ? `${attr.label} *` : attr.label;
	wrap.appendChild(label);

	const value = data.attributes[attr.name];
	let input;

	if(attr.dataType === "code" && attr.codeListId){
		input = document.createElement("select");
		const emptyOption = document.createElement("option");
		emptyOption.value = "";
		emptyOption.textContent = "-";
		input.appendChild(emptyOption);
		const codeList = modelProvider.getCodeList(data.model, data.version, attr.codeListId) || [];
		for(const code of codeList){
			const option = document.createElement("option");
			option.value = code.code;
			option.textContent = code.label;
			input.appendChild(option);
		}
		input.value = value !== undefined && value !== null ? String(value) : "";
	}else if(attr.dataType === "boolean"){
		input = document.createElement("input");
		input.type = "checkbox";
		input.checked = value === true;
	}else if(attr.dataType === "date"){
		input = document.createElement("input");
		input.type = "date";
		input.value = value || "";
	}else{
		input = document.createElement("input");
		input.type = attr.dataType === "number" ? "number" : "text";
		input.value = value !== undefined && value !== null ? value : "";
	}
	input.style.cssText += "max-width:130px; font-size:12px; box-sizing:border-box;";

	input.addEventListener("change", () => {
		let newValue;
		if(attr.dataType === "boolean") newValue = input.checked;
		else if(attr.dataType === "number") newValue = input.value === "" ? undefined : Number(input.value);
		else newValue = input.value === "" ? undefined : input.value;

		setDanvaAttributes(dm.featureManager, feature, {[attr.name]: newValue}, "user-entered");
		renderDanvaPanelContent(root, dm);
	});

	wrap.appendChild(input);
	return wrap;
}

function renderSelectedObject(container, dm, feature, visual){
	container.innerHTML = "";

	if(!isDanvaObject(feature)){
		renderRegistrationOffer(container, dm, feature, visual);
		return;
	}

	const data = getDanvaData(feature);

	const idBox = document.createElement("div");
	idBox.appendChild(row("Navn", feature.properties.name));
	idBox.appendChild(row("Objekt-id", feature.id));
	container.appendChild(section("Identifikation", idBox));

	const modelBox = document.createElement("div");
	modelBox.appendChild(row("Model", `${data.model} ${data.version}`));
	modelBox.appendChild(row("Objekttype", data.objectType));
	modelBox.appendChild(row("DANVA-guid", data.guid));
	const unregisterBtn = button("Fjern DANVA-registrering", () => {
		unclassifyDanva(dm.featureManager, feature.id);
		renderDanvaPanelContent(container, dm);
	});
	modelBox.appendChild(unregisterBtn);
	container.appendChild(section("DANVA-model/type", modelBox));

	const attrBox = document.createElement("div");
	const def = modelProvider.getObjectDefinition(data.model, data.version, data.objectType);
	if(!def){
		attrBox.appendChild(row("", "Ingen modeldefinition indlæst - kan ikke vise/redigere attributter endnu."));
	}else{
		for(const attr of def.attributes) attrBox.appendChild(renderAttributeRow(dm, feature, data, attr, container));
	}
	container.appendChild(section("Grunddata", attrBox));

	container.appendChild(section("Relationer", renderRelationsSection(dm, feature, container)));

	const validationBox = document.createElement("div");
	const geo = validateGeometry(feature);
	validationBox.appendChild(row(`${statusIcon(geo.valid)} Geometri`, geo.valid ? "OK" : geo.issues.map((i) => i.message).join("; ")));
	const logical = validateDanvaObject(feature, modelProvider);
	if(logical.reason === "model-unavailable"){
		validationBox.appendChild(row("⚠ DANVA-data", logical.message));
	}else{
		validationBox.appendChild(row(`${statusIcon(logical.valid)} DANVA-data`, logical.valid ? "OK" : logical.issues.map((i) => i.message).join("; ")));
	}
	container.appendChild(section("Validering", validationBox));
}

function renderDanvaPanelContent(container, dm){
	container.setAttribute("data-danva-root", "1");

	const selectedVisual = dm.selectedVisual;
	const feature = selectedVisual ? dm.getFeature(selectedVisual) : null;

	container.innerHTML = "";
	if(feature){
		renderSelectedObject(container, dm, feature, selectedVisual);
	}else{
		renderProjectOverview(container, dm);
	}
}

// Mounts the DANVA Workspace panel into `container` (a plain DOM element -
// see SideRail.js's registerPanel() contract). Re-renders on selection
// change and on any feature_changed event (covers DANVA classification/
// attribute/relation edits, all of which go through FeatureManager -
// task section 26: reuse existing events, no new DANVA-specific ones).
export function mountDanvaPanel(container, dm){
	const rerender = () => renderDanvaPanelContent(container, dm);

	dm.addEventListener("visual_selection_changed", rerender);
	dm.featureManager.addEventListener("feature_changed", rerender);
	dm.featureManager.addEventListener("feature_added", rerender);
	dm.featureManager.addEventListener("feature_removed", rerender);

	rerender();

	return {
		unmount(){
			stopRelationPicking(dm);
			dm.removeEventListener("visual_selection_changed", rerender);
			dm.featureManager.removeEventListener("feature_changed", rerender);
			dm.featureManager.removeEventListener("feature_added", rerender);
			dm.featureManager.removeEventListener("feature_removed", rerender);
		},
	};
}

export function _getDanvaModelProviderForTesting(){
	return modelProvider;
}
